import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalFooterButtons,
  ModalHeader,
  Select,
  Spinner,
  Text,
  Tooltip,
} from '@televet/kibble-ui';
import { openTrupanionPortal, ProcessStep } from './TrupanionCheckout';
import { SpeciesType, TrupanionAvailability } from 'shared/types/graphql';
import { trupanionCanineBreeds, trupanionFelineBreeds } from '../trupanionBreedList';
import PetAvatar from 'shared/components/Avatars/PetAvatar';

type TrupanionModalProps = {
  isSmsOptedIn: boolean;
  isOpen: boolean;
  closeModal: () => void;
  onSendOffers: () => void;
  processStep: ProcessStep;
  selectedPets: TrupanionAvailability[];
  resetTrupanionState: () => void;
  certificateReferences: { [clinicPetId: string]: string };
  isCertificateError: boolean;
  eligiblePets: TrupanionAvailability[];
  deselectPet: (clinicPetId: string) => void;
  selectPet: (clinicPetId: string) => void;
  selectedBreedValues: {
    [key: string]: string;
  };
  setSelectedBreedValues: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string;
    }>
  >;
  toggleSmsOptIn: () => void;
};

const TrupanionModal = ({
  isSmsOptedIn,
  toggleSmsOptIn,
  selectedBreedValues,
  setSelectedBreedValues,
  selectPet,
  deselectPet,
  eligiblePets,
  isOpen,
  resetTrupanionState,
  closeModal,
  onSendOffers,
  selectedPets,
  processStep,
}: TrupanionModalProps): JSX.Element => {
  const submitButtonText = useMemo(() => {
    return processStep === ProcessStep.Confirmation
      ? 'Done'
      : `Send ${selectedPets.length} Offer${selectedPets.length === 1 ? '' : 's'}`;
  }, [processStep, selectedPets.length]);

  const renderModalBody = (): JSX.Element => {
    switch (processStep) {
      case ProcessStep.Initial:
        return <></>;

      case ProcessStep.IssuingCertificate:
        return <Text>Issuing certificates...</Text>;

      case ProcessStep.Loading:
        return (
          <Flex alignItems="center">
            <Spinner size="md" />
            <Text ml={3}>Sending offer... </Text>
          </Flex>
        );

      case ProcessStep.PetSelection:
        if (eligiblePets.length > 0) {
          return (
            <Flex flexDirection="column">
              <Text>Which pet(s) should receive the offer?</Text>

              {eligiblePets.map((certificate) => {
                if (certificate) {
                  const isPetSelected = selectedPets.some((pet) => pet.clinicPetId === certificate.clinicPetId);
                  const trupanionBreedList =
                    certificate.speciesType === SpeciesType.Canine ? trupanionCanineBreeds : trupanionFelineBreeds;
                  return (
                    <Flex
                      flex="1"
                      key={certificate?.clinicPetId}
                      alignItems="center"
                      justifyContent="space-between"
                      borderColor={isPetSelected ? 'border.focus' : 'border.default'}
                      borderWidth={3}
                      borderRadius="md"
                      marginY={1}
                      padding={2}
                      cursor="pointer"
                      onClick={(e): void => {
                        e.preventDefault();
                        if (isPetSelected) {
                          deselectPet(certificate.clinicPetId);
                        } else {
                          selectPet(certificate.clinicPetId);
                        }
                      }}
                    >
                      <Flex flex="0 0 auto" alignItems="center">
                        <Box flex="0 0 auto">
                          <Checkbox isChecked={isPetSelected} />
                        </Box>
                        <PetAvatar
                          flex="0 0 auto"
                          ml={2}
                          speciesType={certificate.speciesType ?? SpeciesType.Other}
                          isActive
                          isDeceased={false}
                        />
                        <Text flex="0 0 auto" isTruncated>
                          {certificate.clinicPetName}
                        </Text>
                      </Flex>
                      <Flex flex="0 1 auto" alignItems="center" ml={2}>
                        {certificate.pimsBreed !== certificate.trupanionClosestBreed && (
                          <Tooltip
                            label={`PIMS breed, ${certificate.pimsBreed}, does not match Trupanion list`}
                            titleCaseLabel={false}
                          >
                            <Flex alignItems="center" marginX={2}>
                              <Icon name="warningSign" variant="warning" />
                            </Flex>
                          </Tooltip>
                        )}
                        <Select
                          minW="132px"
                          value={
                            selectedBreedValues[certificate.clinicPetId] || certificate.trupanionClosestBreed || ''
                          }
                          options={trupanionBreedList.map((breedName) => {
                            return { label: breedName, value: breedName };
                          })}
                          listProps={{
                            isSearchable: true,
                            onSelect: (breed): void => {
                              console.log('breed', breed);
                              setSelectedBreedValues((prevState) => {
                                const newState = { ...prevState };
                                newState[certificate.clinicPetId] = breed.value || '';
                                return newState;
                              });
                            },
                          }}
                        />
                      </Flex>
                    </Flex>
                  );
                }
              })}
              <Flex flexDirection="column" my={4}>
                <Box>
                  <Text as="p">All offers are sent via email with the option to send it via SMS text below.</Text>
                </Box>
                <Box
                  mt={3}
                  onClick={(e): void => {
                    e.preventDefault();
                    toggleSmsOptIn();
                  }}
                  alignContent="center"
                >
                  <Checkbox isChecked={isSmsOptedIn} alignContent="center">
                    <Text>Also send offer via SMS text</Text>
                  </Checkbox>
                </Box>
              </Flex>
            </Flex>
          );
        }
        return (
          <Flex flex="1" mt={2} justifyContent="center" alignItems="center" flexDirection="column">
            <Text marginY={2} as="p">
              Oops we are unable to determine eligibility.
            </Text>
            <Button size="md" onClick={openTrupanionPortal}>
              Open Trupanion Portal
            </Button>
          </Flex>
        );
    }
    return <></>;
  };

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={(): void => {
        closeModal();
        resetTrupanionState();
      }}
    >
      <ModalHeader>Exam Day Offer</ModalHeader>
      <ModalBody>{renderModalBody()}</ModalBody>
      <ModalFooterButtons
        onCancel={closeModal}
        onSubmit={onSendOffers}
        isDisabled={!selectedPets.length}
        isLoading={processStep === ProcessStep.Loading}
        submitText={submitButtonText}
      />
    </Modal>
  );
};

export default TrupanionModal;
